import classNames from 'classnames';
import { find, get } from 'lodash';
import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import {
	elementBeanListType,
	publishElementsDataType,
} from '../../../Pages/Flo/Flo.types';
import { calculateAspectRatioFit } from '../../../utils/video.utils';
import Video from '../Video';
import styles from './RecordingElement.module.css';

const shapeToDimensionsMap = {
	circle: {
		width: 200,
		height: 200,
	},
	square: {
		width: 200,
		height: 200,
	},
	wide: {
		width: 200,
		height: 150,
	},
	portrait: {
		width: 150,
		height: 200,
	},
};

const DEFAULTDIMENSIONS = {
	width: 128,
	height: 96,
};

const RecordingElement = ({
	currentElement,
	currentElementIndex,
	autoPlay,
	goToStep,
	playerDim,
	hasNotPlayed,
	setCountDownPause,
	isWidthLessThan650,
	isDocument,
	elementVoiceResources,
	togglePause,
	parentLoading,
	floId,
	floIndex,
	disableGotoStep,
	hideElement,
	videoNode,
	isMuted,
	setRecordingSubtitle,
}: {
	setCountDownPause?(value: boolean): void;
	togglePause?(value: boolean): void;
	currentElementIndex?: number;
	currentElement?: publishElementsDataType | null;
	goToStep?(currentElementId?: string, next?: boolean, arrowClick?: boolean): void;
	playerDim: {
		width: number;
		height: number;
	};
	hasNotPlayed?: boolean;
	isWidthLessThan650?: boolean;
	isDocument?: boolean;
	elementVoiceResources?:
		| { [key: string]: { voiceId: string; scriptId: string } }
		| undefined;
	parentLoading?: boolean;
	floId: string;
	floIndex: number;
	autoPlay?: boolean;
	disableGotoStep?: boolean;
	hideElement(): void;
	videoNode?: HTMLVideoElement | null;
	isMuted?: boolean;
	setRecordingSubtitle(val: string): void;
}) => {
	const [defaultPlayerOptions, setDefaultOptions] = useState<videojs.PlayerOptions>({
		controls: false,
		fluid: false,
		controlBar: {
			remainingTimeDisplay: true,
			durationDisplay: true,
			currentTimeDisplay: true,
			fullscreenToggle: false,
			pictureInPictureToggle: false,
			volumePanel: {
				inline: false,
			},
		},
		userActions: {
			doubleClick: false,
		},
	});
	const [containerDims, setContainerDims] = useState({
		width: 80,
		height: 0,
	});
	const [shapeDimensions, setShapeDimensions] = useState({
		width: 0,
		height: 0,
	});
	const [playerOptions, setPlayerOptions] = useState({});
	const [currentTime, setCurrentTime] = useState<number>(0);

	useEffect(() => {
		if (!get(currentElement, 'subTitlesEnabled')) return;
		const subtitle = find(
			get(currentElement, 'subtitlesList', []),
			(item) => currentTime > item.startTime && currentTime <= item.endTime
		);
		setRecordingSubtitle(subtitle?.text);
	}, [
		currentTime,
		setRecordingSubtitle,
		get(currentElement, 'subtitlesList'),
		get(currentElement, 'subTitlesEnabled'),
	]);

	useEffect(() => {
		let defaultDimensions =
			shapeToDimensionsMap[get(currentElement, 'shape')] || DEFAULTDIMENSIONS;
		if (
			defaultDimensions.width < playerDim.width * 0.2 ||
			defaultDimensions.height < playerDim.height * 0.2 ||
			defaultDimensions.height > playerDim.height * 0.5 ||
			defaultDimensions.width > playerDim.width * 0.5
		) {
			defaultDimensions = calculateAspectRatioFit(
				defaultDimensions.width,
				defaultDimensions.height,
				playerDim.width * 0.3,
				playerDim.height * 0.3
			);
		}
		setShapeDimensions(defaultDimensions);
	}, [get(currentElement, 'shape'), playerDim]);

	useEffect(() => {
		const track = {
			src: get(currentElement, 'recordingVideoUrl'),
			withCredentials: false,
			hasThumbnails: false,
			isHls: true,
			thumbnailsPath: '',
			id: get(currentElement, 'id'),
			width: get(currentElement, 'recordingVideoDimensions.width') || 640,
			height: get(currentElement, 'recordingVideoDimensions.height') || 480,
		};
		setPlayerOptions({
			sources: [track],
			loop: false,
		});

		let dims = calculateAspectRatioFit(
			get(currentElement, 'recordingVideoDimensions.width') || 640,
			get(currentElement, 'recordingVideoDimensions.height') || 480,
			shapeDimensions.width,
			shapeDimensions.height
		);
		if (dims.width < shapeDimensions.width || dims.height < shapeDimensions.height) {
			dims = calculateAspectRatioFit(
				dims.width,
				dims.height,
				shapeDimensions.width,
				shapeDimensions.height,
				true
			);
		}
		setContainerDims(dims);
		setDefaultOptions((option) => ({ ...option, ...dims }));
	}, [
		get(currentElement, 'recordingVideoDimensions.width'),
		get(currentElement, 'recordingVideoDimensions.height'),
		get(currentElement, 'recordingVideoUrl'),
		get(currentElement, 'shape'),
		shapeDimensions,
	]);

	useEffect(() => {
		if (videoNode?.current) {
			videoNode.current.onended = () => {
				if (!disableGotoStep) {
					goToStep?.(undefined, true);
					return;
				}
				hideElement();
			};
		}
	}, [videoNode?.current, goToStep, disableGotoStep, hideElement]);

	const onSetReadyCallback = useCallback(
		(val) => {
			// setVideoReady(val);
			if (val && videoNode?.current) {
				videoNode.current.play().catch((err) => {
					videoNode.current.muted = true;
					videoNode.current.play();
				});
			}
		},
		[videoNode?.current]
	);

	useEffect(() => {
		if (!get(currentElement, 'subTitlesEnabled')) return;
		const interval = setInterval(() => {
			setCurrentTime(videoNode?.current?.currentTime);
		}, 200);
		return () => {
			if (interval) clearInterval(interval);
		};
	}, [videoNode?.current, get(currentElement, 'subTitlesEnabled')]);

	if (!currentElement) return null;

	return (
		<div
			className={classNames(
				styles.recordingContainer,
				styles[get(currentElement, 'position')]
			)}
			// style={{
			// 	height: vHeight - offsetTop * 2,
			// 	width: vWidth - offsetLeft * 2,
			// 	top: offsetTop,
			// }}
		>
			<div
				className={classNames(
					styles.recordingVideoContainer,
					styles[get(currentElement, 'shape')]
				)}
				style={shapeDimensions}
			>
				<div style={containerDims} className={styles.recordingVideoWrapper}>
					<Video
						addWrapper
						mute={isMuted}
						style={containerDims}
						//@ts-ignore
						nodeRef={videoNode}
						options={playerOptions}
						className={styles.recordingVideo}
						defaultOptions={defaultPlayerOptions}
						setReady={onSetReadyCallback}
						//@ts-ignore
						defaultMute={autoPlay}
						autoPlay={autoPlay && currentElementIndex === -1}
						ignoreThumbnail
						ignoreCropAndTrim
						floId={floId}
						floIndex={floIndex}
					/>
				</div>
			</div>
		</div>
	);
};

export default RecordingElement;
